import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
  Fab,
  Snackbar,
  Alert,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AddIcon from '@mui/icons-material/Add';

// サンプルデータ
const sampleQuestions = [
  {
    id: 1,
    question: "普段の休日の過ごし方を教えてください！",
    timestamp: "2024-01-24T10:30:00",
    status: "unanswered",
    isStarred: true,
    anonymous: true
  },
  {
    id: 2,
    question: "最近ハマっている音楽はありますか？おすすめの曲があれば教えてください！",
    timestamp: "2024-01-24T09:15:00",
    status: "answered",
    isStarred: false,
    anonymous: false,
    username: "music_lover"
  },
  {
    id: 3,
    question: "好きな旅行先と、その理由を教えていただけますか？",
    timestamp: "2024-01-23T18:45:00",
    status: "unanswered",
    isStarred: true,
    anonymous: true
  }
];

const QuestionCard = ({ question }) => {
  const theme = useTheme();

  const getStatusColor = (status) => {
    return status === 'unanswered' ? theme.palette.error.main : theme.palette.success.main;
  };

  const getStatusLabel = (status) => {
    return status === 'unanswered' ? '未回答' : '回答済み';
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('ja-JP', {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  return (
    <Card
      sx={{
        mb: 2,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center">
            {question.anonymous ? (
              <Avatar sx={{ bgcolor: theme.palette.grey[300], mr: 1 }}>
                <QuestionAnswerIcon fontSize="small" />
              </Avatar>
            ) : (
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1 }}>
                {question.username?.[0].toUpperCase()}
              </Avatar>
            )}
            <Typography variant="body2" color="text.secondary">
              {question.anonymous ? '匿名' : question.username}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              size="small"
              label={getStatusLabel(question.status)}
              sx={{
                backgroundColor: getStatusColor(question.status),
                color: 'white'
              }}
            />
            <IconButton 
              size="small"
              sx={{ 
                color: question.isStarred ? theme.palette.warning.main : 'inherit'
              }}
            >
              {question.isStarred ? (
                <StarIcon fontSize="small" />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton size="small">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="body1" mb={2}>
          {question.question}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          {formatDate(question.timestamp)}
        </Typography>
      </CardContent>
    </Card>
  );
};

// 質問送信ダイアログコンポーネント
const QuestionDialog = () => {
    // ダイアログの表示状態を管理
    const [open, setOpen] = useState(false);
    // フォームの入力値を管理
    const [question, setQuestion] = useState('');
    // 匿名投稿の状態を管理
    const [isAnonymous, setIsAnonymous] = useState(true);
    // 送信中の状態を管理
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 送信完了メッセージの表示状態を管理
    const [showSuccess, setShowSuccess] = useState(false);
  
    // ダイアログを開く
    const handleOpen = () => {
      setOpen(true);
    };
  
    // ダイアログを閉じる
    const handleClose = () => {
      setOpen(false);
      // フォームの状態をリセット
      setQuestion('');
      setIsAnonymous(true);
    };
  
    // 質問を送信
    const handleSubmit = async () => {
      if (!question.trim()) {
        return;
      }
  
      setIsSubmitting(true);
      
      try {
        // ここで実際のAPIコールを行う
        await new Promise(resolve => setTimeout(resolve, 1000)); // デモ用の遅延
  
        // 送信成功時の処理
        setShowSuccess(true);
        handleClose();
      } catch (error) {
        console.error('質問の送信に失敗しました:', error);
        // エラー処理を追加
      } finally {
        setIsSubmitting(false);
      }
    };
  
    return (
      <>
        {/* 質問送信ボタン */}
        <Fab
          color="primary"
          aria-label="質問を送る"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32
          }}
        >
          <AddIcon />
        </Fab>
  
        {/* 質問送信ダイアログ */}
        <Dialog 
          open={open} 
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>質問を送る</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              multiline
              rows={4}
              label="質問内容"
              fullWidth
              variant="outlined"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="質問を入力してください..."
              error={question.trim() === ''}
              helperText={question.trim() === '' && '質問を入力してください'}
            />
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    color="primary"
                  />
                }
                label="匿名で質問する"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>
              キャンセル
            </Button>
            <Button 
              onClick={handleSubmit}
              variant="contained"
              disabled={!question.trim() || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? '送信中...' : '送信する'}
            </Button>
          </DialogActions>
        </Dialog>
  
        {/* 送信完了メッセージ */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={6000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={() => setShowSuccess(false)} 
            severity="success"
            elevation={6}
            variant="filled"
          >
            質問を送信しました
          </Alert>
        </Snackbar>
      </>
    );
  };

const QuestionListPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="md">
      <Box py={4}>
        {/* ヘッダー */}
        <Typography variant="h4" component="h1" gutterBottom>
          質問一覧
        </Typography>

        {/* 検索とフィルター */}
        <Box display="flex" gap={2} mb={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="質問を検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>並び順</InputLabel>
            <Select
              value={sortBy}
              label="並び順"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="newest">新着順</MenuItem>
              <MenuItem value="oldest">古い順</MenuItem>
              <MenuItem value="starred">スター付き</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* タブ */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="すべて" />
            <Tab label="未回答" />
            <Tab label="回答済み" />
          </Tabs>
        </Box>

        {/* 質問リスト */}
        <Box>
          {sampleQuestions.map((question) => (
            <QuestionCard key={question.id} question={question} />
          ))}
        </Box>
      </Box>
      <QuestionDialog />
    </Container>
  );
};

export default QuestionListPage;